// Japanese (です/ます) copy for the pricing pages. Same shape as copy.en.ts (the source of truth); keep the two in
// step. Plan names (Lite, Pro, Max, Pay as you go, Enterprise) are product names and stay in English, as they do
// in the app. "オンデマンドFTP", "Webアップローダー" and "プレビュー先行" as on /on-demand-ftp and
// /preview-first-uploads; the commission is "販売手数料", card fees "カード手数料".

import type { PricingCopy } from "./copy.en";

const ja: PricingCopy = {
  common: {
    perMonth: "/月",
    onDemandFtpLink: "オンデマンドFTPの仕組み",
    aMonth: "/月",
    today: "現在",
    fromChangeDate: "11月1日から",
    changeDate: "2026年11月1日",
    gb: "{n} GB",
    tb: "{n} TB",
    mostPopular: "いちばん人気",
    renamedPlan: "{from}（現在の名称は{to}）",
    planFeatures: {
      free: [
        {
          text: "独自ブランディング（“Powered by Photohawk”表示あり）",
          note: "すべてのギャラリーにロゴとブランドカラーを表示できます。ギャラリーとワークスペースのログインページの下部に、小さな“Powered by Photohawk”バッジが表示されます。Pro と Max にはバッジは表示されません。",
        },
        "最大3人のチーム",
        "ヘルプセンターによるサポート",
        {
          text: "オンデマンドFTPサーバー",
          note: "FTPが欠かせない場合は、Webアップローダーから自分専用のサーバーを起動できます。アドレスは変わらない自分専用のPhotohawkアドレスです。操作がないまま1時間たつと自動で停止します。",
        },
        {
          text: "プレビュー先行アップロード",
          note: "軽量プレビューで数分のうちにギャラリーを公開し、オリジナルは後から届きます。販売にも無料ダウンロードにも使えます。",
        },
      ],
      pro: [
        {
          text: "独自ブランディング",
          note: "ロゴとブランドカラーを表示でき、Photohawk のバッジは表示されません。",
        },
        "独自ドメイン",
        "最大10人のチーム",
        "訪問者分析",
        {
          text: "オンデマンドFTPサーバー",
          note: "FTPが欠かせない場合は、Webアップローダーから自分専用のサーバーを起動できます。アドレスは変わらない自分専用のPhotohawkアドレスです。操作がないまま1時間たつと自動で停止します。",
        },
        {
          text: "プレビュー先行アップロード",
          note: "軽量プレビューで数分のうちにギャラリーを公開し、オリジナルは後から届きます。販売にも無料ダウンロードにも使えます。",
        },
        "メールサポート",
      ],
      max: [
        "Pro のすべての機能",
        "動画のアップロード",
        "売上の分配",
        "複数ブランド",
        "チームメンバーとロールが無制限",
        "訪問者分析",
        {
          text: "オンデマンドFTPサーバー",
          note: "FTPが欠かせない場合は、Webアップローダーから自分専用のサーバーを起動できます。アドレスは変わらない自分専用のPhotohawkアドレスです。操作がないまま1時間たつと自動で停止します。",
        },
        {
          text: "プレビュー先行アップロード",
          note: "軽量プレビューで数分のうちにギャラリーを公開し、オリジナルは後から届きます。販売にも無料ダウンロードにも使えます。",
        },
        "優先サポート",
      ],
    },
    planTaglines: {
      free: "はじめての方や、ときどきイベントを撮影する方に。",
      pro: "自社ブランドで定期的に販売するワークスペースに。",
      max: "スタジオ、チーム、大量販売に。いちばん低い販売手数料、最大のストレージ、動画、優先サポート。",
    },
    countries: {
      uk: "イギリス",
      my: "マレーシア",
      ph: "フィリピン",
      mx: "メキシコ",
      co: "コロンビア",
      eur: "ポルトガルとユーロ圏",
      us: "アメリカ",
      ca: "カナダ",
    },
    inCountry: {
      uk: "イギリスでの",
      my: "マレーシアでの",
      ph: "フィリピンでの",
      mx: "メキシコでの",
      co: "コロンビアでの",
      eur: "ポルトガルとユーロ圏での",
      us: "アメリカでの",
      ca: "カナダでの",
    },
  },

  pricing: {
    seo: {
      title: "料金 | Photohawk",
      description:
        "イベント写真の販売手数料がすべてのプランで下がります。Lite は16%、Pro は14%、Max は12%。ストレージと無料ダウンロード込み。",
    },
    notice: "2026年11月1日から新料金になります。",
    noticeLink: "変更内容を見る",
    hero: {
      badge: "料金",
      title: "すべてのプランで販売手数料が下がります。",
      titleHighlight: "最低{rate}から。",
      subtitle:
        "販売手数料は Lite で16%、Pro で14%、Max で12%。決済手数料の仕組みは現在と変わりません。",
      events: "マラソンや自転車レース、試合、公演、フェスティバル、卒業式、カンファレンスまで、どんなイベントにも同じプランで対応します。",
    },
    rate: {
      heading: "プランごとの販売手数料",
      planLabel: "{plan}",
      points: [
        "カード手数料は別で、現在と同じ仕組みです。",
        "販売時に Photohawk がいただくのは販売手数料のみです。",
        "既存の個別契約は、期間終了まで引き継がれます。",
      ],
    },
    plans: {
      heading: "プランを選ぶ",
      subtitle:
        "米ドル建ての月額プランで、契約期間の縛りはありません。上位プランほど販売手数料が下がり、ストレージと無料ダウンロードが増えます。",
      sellingLabel: "販売",
      sellingLine: "販売手数料{rate}",
      sellingNote: "別途決済手数料（現在と同じ）",
      freeDownloadsLabel: "無料ダウンロード",
      freeDownloadsLine: "毎月{count}枚まで込み",
      overageLine: "超過分は1,000枚ごとに$8",
      storageLabel: "ストレージ",
      storageLine: "{storage}込み",
      storageNote: "超過分は月$5からのパック",
      includedLabel: "含まれるもの",
      cta: "始める",
      ctaFor: "（{plan}）",
      planChange:
        "プラン変更：販売手数料の低いプランに変えると、そのプランでの最初の丸1か月から新しい手数料が適用されます。ダウングレードは月末に反映されます。",
    },
    standard: {
      heading: "すべてのプランに標準搭載",
      subtitle: "月額$0の Lite を含め、すべてのプランに含まれます。",
      items: [
        { id: "selfie", title: "セルフィー検索", body: "購入者がセルフィーを撮るだけで、自分が写っている写真をすべて見られます。" },
        { id: "bib", title: "ゼッケン番号・時刻検索", body: "ゼッケン番号や撮影時刻で写真を探せます。" },
        { id: "preview", title: "プレビュー保護", body: "タップで表示するスクリーンショット対策で、プレビューはコピーされずに売上につながります。" },
        {
          id: "uploader",
          title: "Webアップローダーと Photohawk Studio",
          body: "アップロードの標準の方法です。多数の写真を同時に送信し、回線に合わせて調整し、途切れても自動で再開します。すべてのプランで軽量プレビューを先に送れるので、数分でギャラリーを公開できます。Studio は現在 Web ブラウザで動作し、Mac、iPhone、iPad 向けの Photohawk Studio アプリは2026年11月にリリース予定です。",
        },
        { id: "whatsapp", title: "WhatsApp 配信（購入者向けの Photohawk サービス）", body: "購入者はチェックアウト時に少額を追加で支払うと、写真を WhatsApp で受け取ってすぐに共有できます。Photohawk がすべてのギャラリーで提供しているため、設定、価格付け、オフにする操作は不要です。" },
      ],
    },
    storage: {
      heading: "ストレージ",
      subtitle:
        "すべてのプランにストレージが含まれます。それ以上保存する場合、カードが登録されていればパックが自動で追加され、保存量をまかなう最も安い組み合わせで請求されます。カードが未登録の場合は、登録するまで新しいアップロードが一時停止します。データが削除されることはなく、ギャラリーでの販売も続きます。",
      includedHeading: "含まれる容量",
      packsHeading: "追加ストレージパック",
      packPrice: "月${price}",
      examplesHeading: "パックの組み合わせ例",
      example: "{plan}で{stored}を保存：{over}超過、{mix}として請求 = 月${price}",
      note: "ストレージの80%に達するとメールでお知らせします。空き容量を増やすとパックは外れます。",
    },
    freeDownloads: {
      heading: "無料ダウンロードのギャラリー",
      subtitle:
        "購入者が無料でダウンロードできるギャラリーを共有できます。各プランに毎月一定枚数が含まれ、超過分はどのプランでも1,000枚ごとに$8（1枚あたり$0.008）です。",
      line: "毎月{count}枚",
      note: "枠は毎月リセットされ、枠を超えてもギャラリーはそのまま使えます。",
    },
    example: {
      heading: "手元に残る金額",
      subtitle: "イギリスでカード決済された注文を、ご自身の Stripe アカウントで受け取った場合のプラン別の例です。金額を変えて試せます。",
      orderLabel: "注文金額",
      order: "注文",
      cardFee: "Stripe のカード手数料",
      cardFeeNote: "Stripe からお客様のアカウントに請求されます（例：イギリスのカードは1.5% + 20p）",
      colPlan: "プラン",
      colCommission: "Photohawk 販売手数料",
      colKeep: "手元に残る金額（目安）",
      planCell: "{plan}（{rate}）",
      note: "Stripe の手数料はカードによって異なり、海外カードや通貨換算は通常より高くなります。Photohawk がいただくのは販売手数料のみです。",
    },
    cardFees: {
      heading: "カード手数料の仕組み",
      intro: "カード手数料と決済処理手数料は、販売手数料とは別で、現在と同じ仕組みです。",
      items: [
        {
          id: "stripe",
          title: "ご自身の Stripe アカウントの場合",
          body: "イギリス、EU、アメリカなど Stripe 対応国では、Stripe が標準の決済処理手数料をお客様の Stripe アカウントに直接請求します。",
        },
        {
          id: "managed",
          title: "Photohawk が決済を代行する場合",
          body: "マレーシア、フィリピン、メキシコ、コロンビア、エクアドルなど、購入者が現地のウォレットで支払う国では、現在と同様に、実費に基づく現地料金で決済処理手数料が差し引かれます。",
        },
      ],
      linkLabel: "国別のカード手数料を見る",
    },
    compare: {
      heading: "すべての機能を比較",
      subtitle: "Photohawk Studio のすべての機能をプランごとに比較できます。Studio は現在、すべてのプランで Web ブラウザから使えます。",
      featureColumn: "機能",
      included: "含まれる",
      notIncluded: "含まれない",
      linkLabel: "すべての機能を比較",
      sections: {
        selling: "販売",
        finding: "写真を探す",
        galleries: "ギャラリー",
        brand: "ブランド",
        insights: "分析",
        team: "チーム",
        uploading: "アップロード",
        storage: "ストレージ",
        support: "サポート",
      },
      rows: {
        commission: { label: "販売手数料", note: "販売時にいただくのはこの手数料のみです。カード手数料は現在と同じ仕組みです。" },
        products: { label: "デジタルダウンロード、プリント、商品", note: "独自の商品と価格を設定できます。" },
        packages: { label: "パッケージとセット販売", note: "" },
        coupons: { label: "クーポン", note: "" },
        preorder: { label: "予約注文価格", note: "" },
        whatsapp: { label: "WhatsApp 配信（購入者向けの Photohawk サービス）", note: "購入者がチェックアウト時に追加で支払うオプションで、写真が WhatsApp に届きすぐに共有できます。Photohawk がすべてのギャラリーで提供しており、設定で切り替えるものではありません。" },
        payouts: { label: "入金", note: "ご自身の Stripe アカウントへ、または Photohawk が決済を代行する場合は Photohawk から入金されます。" },
        revenueSplit: { label: "売上の分配", note: "ギャラリーの売上をフォトグラファー、チーム、クラブ、スポンサーと分け合えます。各取り分は Photohawk が計算し、代わりに支払います。" },
        selfie: { label: "セルフィー検索", note: "" },
        bib: { label: "ゼッケン番号・テキスト検索", note: "ゼッケン番号や写真内のテキストで写真を探せます。" },
        time: { label: "時刻検索", note: "" },
        find: { label: "「写真を探す」への掲載", note: "購入者は photohawk.com で公開ギャラリーを見つけられます。" },
        profiles: { label: "保存した検索", note: "セルフィー、番号、テキストを保存して、新しく一致した写真を確認できます。" },
        faceData: { label: "セルフィー検索の顔データ", note: "2026年11月1日から、既定は「期限なし」、または選んだ期間で保持します。" },
        galleries: { label: "ギャラリーとコレクション", note: "必要なだけ作成できます。" },
        private: { label: "非公開ギャラリーとパスワード", note: "" },
        protection: { label: "プレビュー保護", note: "タップで表示するスクリーンショット対策。" },
        watermarks: { label: "透かし", note: "" },
        freeDownloads: { label: "含まれる無料ダウンロード枚数", note: "枠は毎月リセットされます。" },
        freeOverage: { label: "枠を超えた無料ダウンロード", note: "ギャラリーはそのまま使えます。" },
        video: { label: "動画のアップロード", note: "" },
        address: { label: "Photohawk アドレス", note: "yourname.photohawk.com" },
        customDomain: { label: "独自ドメイン", note: "ご自身の Web アドレスでギャラリーを公開できます。" },
        pages: { label: "ページ", note: "ギャラリーと並べて独自のページを作成できます。" },
        emails: { label: "カスタムメールテンプレート", note: "各ギャラリーから購入者に届くメールを編集できます。" },
        logo: { label: "ロゴとブランドカラー", note: "ギャラリー、ページ、メールに表示されます。" },
        noBadge: { label: "“Powered by Photohawk”バッジなし", note: "Lite では、ギャラリーとログインページの下部に小さなバッジが表示されます。" },
        live: { label: "リアルタイム訪問者数", note: "今ギャラリーを見ている人数がわかります。" },
        analytics: { label: "訪問者分析", note: "ギャラリーごとのユーザー数と訪問数の推移。" },
        sales: { label: "注文と売上の推移", note: "" },
        collectionDownloads: { label: "コレクション別ダウンロード数", note: "" },
        people: { label: "ワークスペースのメンバー", note: "ギャラリーの共同作業者も含みます。既にワークスペースにいるメンバーはアクセスを維持できます。" },
        roles: { label: "ロール", note: "オーナー、管理者、アップロードのみ、販売のみのアクセス権限。" },
        collaborators: { label: "ギャラリーの共同作業者", note: "" },
        webUploader: { label: "Webアップローダー", note: "多数の写真を同時に送信し、途切れても自動で再開します。" },
        previewsFirst: { label: "プレビュー先行", note: "軽量プレビューで数分のうちにギャラリーを公開し、オリジナルは後から届きます。" },
        soldFirst: { label: "売れた写真を優先", note: "オリジナルの到着前に購入またはダウンロードされた写真を、次に送ります。" },
        originalsBy: { label: "購入者向けの「オリジナルの提供日」", note: "オリジナルがいつ届くかを購入者に伝えられます。" },
        ftp: { label: "オンデマンドFTPサーバー", note: "FTPが欠かせない場合に、Webアップローダーから起動する自分専用のサーバーです。操作がないまま1時間たつと自動で停止します。" },
        cloudImport: { label: "Dropbox と Google ドライブからの取り込み", note: "" },
        studioApp: { label: "Mac、iPhone、iPad 向け Photohawk Studio アプリ", note: "Studio は現在 Web ブラウザで使えます。" },
        included: { label: "含まれるストレージ", note: "" },
        packs: { label: "追加ストレージ", note: "カード登録済みなら自動で追加され、最も安い組み合わせで請求されます。" },
        you: { label: "ワークスペースへのサポート", note: "" },
        buyers: { label: "購入者への対応は Photohawk が担当", note: "購入者からの問い合わせは Photohawk サポートに届きます（ご自身の連絡先を設定した場合を除く）。" },
      },
      values: {
        rate: "{rate}",
        storage: "{storage}",
        perMonth: "毎月{n}",
        upToPeople: "最大{n}人",
        unlimited: "無制限",
        upToDomains: "最大{n}件",
        months: "{n}か月",
        fromNovember: "2026年11月から",
        packs: "{gb} GBごとに月${usd}から",
        overage: "1,000枚ごとに${usd}",
        supportHelp: "ヘルプセンター",
        supportEmail: "メール",
        supportPriority: "優先",
      },
    },
    faq: {
      heading: "よくある質問",
      items: [
        {
          q: "カード手数料は含まれていますか？",
          a: "いいえ。カード手数料は現在と同じく決済事業者が請求します。Stripe からお客様のアカウントに請求されるか、Photohawk が決済を代行する場合は実費に基づく現地料金で差し引かれます。Photohawk がいただくのは販売手数料（Lite は16%、Pro は14%、Max は12%）のみです。",
        },
        {
          q: "セルフィー検索は含まれていますか？",
          a: "はい。Lite を含むすべてのプランに含まれます。ゼッケン番号・時刻検索、プレビュー保護、Webアップローダーも同様です。Mac、iPhone、iPad 向けの Photohawk Studio アプリも、2026年11月のリリース時からすべてのプランで使えます。",
        },
        {
          q: "プランはいつでも変更できますか？",
          a: "はい。プランは月額制で、年間契約はありません。販売手数料の低いプランに変えると、そのプランでの最初の丸1か月から新しい手数料が適用されます。ダウングレードは月末に反映されます。",
        },
        {
          q: "個別契約を結んでいます。変わりますか？",
          a: "いいえ。既存の個別契約は、期間終了まで引き継がれます。",
        },
        {
          q: "無料ダウンロードが枠を超えるとどうなりますか？",
          a: "無料ダウンロードのギャラリーはそのまま使えます。毎月、Lite は1,000枚、Pro は5,000枚、Max は10,000枚がプランに含まれます。それを超えると、どのプランでも1,000枚ごとに$8（1枚あたり$0.008）です。",
        },
        {
          q: "ストレージが足りなくなったら？",
          a: "Lite は150 GB、Pro は1 TB、Max は3 TB が含まれます。それを超える場合、カードが登録されていればパックが自動で追加されます。+100 GB は$5、+1 TB は$25、+5 TB は$100（いずれも月額）で、保存量をまかなう最も安い組み合わせで請求されます。カードが未登録の場合は、登録するまで新しいアップロードが一時停止します。データが削除されることはなく、ギャラリーでの販売も続きます。",
        },
        {
          q: "Max では何が使えますか？",
          a: "いちばん低い販売手数料12%に加え、3 TB のストレージ、毎月10,000枚の無料ダウンロード、動画のアップロード、無制限のチームメンバー、訪問者分析、優先サポートが使えます。",
        },
        {
          q: "ワークスペースには何人まで参加できますか？",
          a: "Lite は最大3人、Pro は最大10人、Max は無制限です。既にそれ以上のメンバーがいる場合も、アクセスはそのまま維持されます。上限が適用されるのは、新しく誰かを招待するときだけです。",
        },
        {
          id: "ftp",
          q: "FTPでのアップロードは引き続き使えますか？",
          a: "はい、FTPが欠かせない場合は使えます。現在、アップロードの標準はすべてのプランで Webアップローダーです。多数の写真を同時に送信でき、FileZilla（初期設定では同時に2枚、上限は10枚）のようなFTPソフトよりはるかに多く送れるうえ、接続が切れても自動で再開します。それでもFTPが必要な場合は、すべてのプランで、ワークスペースのメンバーは誰でも Webアップローダーから自分専用のオンデマンドFTPサーバーを起動できます。アドレスは変わらない個人用のPhotohawkアドレスで、操作がないまま1時間たつと自動で停止します。共有FTPサーバーは2026年11月1日に終了します。",
        },
        {
          q: "Photohawk Friends & Family はプランに含まれますか？",
          a: "いいえ。Friends & Family はご家族の写真のための当社のアプリで、別のアプリとして近日公開予定です。Studio のプランには含まれません。ギャラリーの運営やアップロードには Photohawk Studio をお使いください。現在すべてのプランで Web ブラウザから使え、Mac、iPhone、iPad 向けの Studio アプリは2026年11月にリリース予定です。",
        },
      ],
    },
  },

  changes: {
    seo: {
      title: "2026年11月1日から料金が変わります | Photohawk",
      description:
        "2026年11月1日から、Photohawk のすべてのプランで販売手数料が下がり、Pro と Max のストレージが増え、毎月の無料ダウンロード枚数が含まれるようになります。",
    },
    hero: {
      badge: "料金改定",
      title: "2026年11月1日から料金が変わります。",
      titleHighlight: "すべてのプランで販売手数料が下がります。",
      subtitle:
        "すべてのプランで販売手数料が下がり、Pro と Max のストレージが増え、無料ダウンロードが毎月一定枚数含まれるようになります。お客様の操作は必要ありません。",
      ctaPrimary: "新しい料金を見る",
      ctaSecondary: "ご利用のプランと比較",
    },
    why: {
      heading: "良くなる点",
      items: [
        {
          id: "rates",
          title: "すべてのプランで販売手数料が下がります",
          body: "すべてのプランで販売手数料が1.5〜9ポイント下がります。Pay as you go と Lite は新しい Lite になり、月額料金なしで16%。Pro は14%に、Enterprise（現在の名称は Max）は12%になります。",
        },
        {
          id: "storage",
          title: "すべてのプランでストレージが増えます",
          body: "Lite は100 GBから150 GBに、Pro は500 GBから1 TBに、Max は1 TBから3 TBになります。Pay as you go の25 GBは、Lite の150 GBになります。",
        },
        {
          id: "downloads",
          title: "無料ダウンロードが含まれます",
          body: "現在は無料ダウンロードのギャラリーのすべての写真に料金がかかります。11月1日からは毎月1,000〜10,000枚が含まれ、超過分は1,000枚ごとに$8です。どのプランでも現在と同じかそれ以下の料金です。",
        },
        {
          id: "simple",
          title: "シンプルな料金体系",
          body: "プランは4つから3つに。販売手数料はプランごとに1つ、追加ストレージはわかりやすいパックで。",
        },
        {
          id: "order",
          title: "決済手数料は変わりません",
          body: "決済手数料は販売手数料とは別で、現在と同じ仕組みです。",
        },
        {
          id: "deals",
          title: "個別契約はそのまま",
          body: "個別契約がある場合は、期間終了まで引き継がれます。",
        },
      ],
      honestHeading: "値上がりする料金はひとつだけ",
      honestBody:
        "Enterprise（現在の名称は Max）は、11月1日以降の最初の更新日から月額$49（これまでは$44.99）になります。その代わり、1 TBではなく3 TBのストレージ、毎月10,000枚の無料ダウンロード、そしていちばん低い販売手数料（13.5%ではなく12%）が含まれます。月の売上が約$270を超えれば、販売手数料の引き下げで差額を上回ります。Pro は$29.99から$29に下がります。",
    },
    when: {
      heading: "11月1日に起きること",
      subtitle: "全員同じ日に切り替わります。ワークスペースは自動で新しいプランに移行します。",
      moves: {
        payg: "引き続き月額$0。販売手数料は25%から16%に、ストレージは25 GBから150 GBになります。",
        lite: "Lite は継続し、無料になります。月額$16.99はかからなくなり、販売手数料は18.5%から16%に、ストレージは100 GBから150 GBに増えます。さらに必要な場合は、追加ストレージを100 GBあたり月$5から利用できます。ギャラリーにはこれまでどおりロゴとブランドカラーが表示され、小さな“Powered by Photohawk”バッジが付きます。",
        pro: "月額$29.99から$29に、販売手数料は16%から14%に、ストレージは500 GBから1 TBになります。",
        enterprise: "Enterprise は Max に名称が変わります。プランは同じで名前だけが変わり、3 TBのストレージと12%の販売手数料になります。11月1日以降の最初の更新日から、月額$44.99ではなく$49になります。",
      },
      points: [
        {
          id: "action",
          title: "お客様の操作は不要です",
          body: "ワークスペースは2026年11月1日に移行します。ギャラリー、価格、入金はこれまでどおり続きます。",
        },
        {
          id: "month",
          title: "丸1か月のルール",
          body: "販売手数料の低いプランに変えると、そのプランでの最初の丸1か月から新しい手数料が適用されます。ダウングレードは月末に反映されます。",
        },
        {
          id: "change",
          title: "プランの変更",
          body: "プランはワークスペースの「請求」からいつでも変更できます。",
        },
        {
          id: "deals",
          title: "個別契約",
          body: "既存の個別契約は、期間終了まで引き継がれます。",
        },
        {
          id: "privacy",
          title: "セルフィー検索とプライバシー",
          body: "11月1日から、セルフィー検索に使う顔データの保持期間を選べるようになります。既定は「期限なし」で、各写真のアップロード後の期間を指定することもできます。写真、売上、ゼッケン番号検索には影響しません。",
        },
        {
          id: "team",
          title: "チーム",
          body: "Lite は最大3人、Pro は最大10人、Max は無制限です。既にそれ以上のメンバーがいる場合も、アクセスはそのまま維持されます。上限が適用されるのは新しくメンバーを追加するときだけです。",
        },
      ],
    },
    ftp: {
      date: "2026年11月1日",
      heading: "アップロードの標準は Webアップローダーになりました",
      body: "常時稼働の共有FTPサーバーは2026年11月1日に終了します。Webアップローダーは FTP より速く、回線を最大限に活用でき、すべてのプランで使えます。FTPが欠かせない場合は、自分専用のサーバーを必要なときに起動できます。",
      items: [
        {
          id: "web",
          title: "Webアップローダー（すべてのプラン）",
          body: "多数の写真を同時に送信でき、FileZilla（初期設定では同時に2枚、上限は10枚）のようなFTPソフトよりはるかに多く送れます。帯域を最大限に使えるよう常に調整し、接続が切れても自動で再開します。すべてのプランで軽量プレビューを先に送れるので、数分でギャラリーを公開できます。",
        },
        {
          id: "ondemand",
          title: "FTPが欠かせない場合も、すべてのプランで",
          body: "提供開始済み、すべてのプランで使えます。Webアップローダーから自分専用のオンデマンドFTPサーバーを起動できます。アドレスは変わらない個人用のPhotohawkアドレスで、操作がないまま1時間たつと自動で停止します。",
        },
        {
          id: "studio",
          title: "Photohawk Studio（すべてのプラン）",
          body: "ワークスペース向けの Photohawk アップロードアプリです。デスクトップと iOS に対応します。",
        },
      ],
    },
    privacyLink: "生体データの保持に関するポリシー",
    included: {
      heading: "各プランに含まれるもの",
      everyPlan:
        "すべてのプラン：セルフィー検索、ゼッケン番号・時刻検索、プレビュー保護、プレビュー先行に対応した Webアップローダー、自分専用のオンデマンドFTPサーバー。",
      upgrades: "Pro と Max では訪問者分析と独自ドメインが加わります。Max ではさらに動画のアップロードが加わります。",
    },
    compare: {
      heading: "現在と11月1日からの比較",
      subtitle: "プラン別、米ドル建て。",
      rows: {
        fee: "月額料金",
        commission: "販売手数料",
        storage: "含まれるストレージ",
        downloads: "無料ダウンロード",
        extraStorage: "追加ストレージ",
      },
      downloadsToday: "1枚あたり{price}",
      downloadsNext: "毎月{count}枚まで込み、超過分は1,000枚ごとに$8",
      extraStorageToday: "1 GBあたり月$0.023",
      extraStorageNext: "パック：+100 GB $5、+1 TB $25、+5 TB $100（月額）",
    },
    countries: {
      heading: "販売する国での影響",
      subtitle: "現地通貨での例です。国を選んでください。",
      selectLabel: "国",
      examplesBadge: "例",
      orderHeading: "一般的な注文：{amount}",
      orderSubtitle: "{inCountry}平均的なカード決済の注文。カード手数料{fee}（{kind}）。",
      kind: {
        stripe: "Stripe からお客様のアカウントに請求",
        managed: "Photohawk が決済を代行する場合に差し引き",
      },
      colPlan: "プラン",
      colCommission: "Photohawk 販売手数料",
      colKeep: "手元に残る金額",
      perOrderMore: "1注文あたり+{amount}",
      monthlyHeading: "1か月の売上で見た総額",
      monthlySubtitle:
        "プラン料金、販売手数料、カード手数料の合計を売上に対する割合で示しています。各売上規模で最も安いプランで計算しています。",
      level: "月の売上 {amount}",
      onPlan: "{plan}の場合",
      keepMore: "毎月約{amount}多く手元に残ります",
      disclaimer:
        "あくまで例であり、平均注文額をもとに固定為替レート（2026年10月）で換算しています。実際の金額は注文、カード、プランによって異なります。無料ダウンロードとストレージの料金は含まれていません。",
    },
    faq: {
      heading: "よくある質問",
      items: [
        {
          q: "何か手続きは必要ですか？",
          a: "いいえ。2026年11月1日に、ワークスペースは自動で新しいプランに移行します。ギャラリー、価格、入金はこれまでどおり続きます。",
        },
        {
          q: "Lite を利用しています。どうなりますか？",
          a: "Lite は継続し、無料になります。11月1日から月額$16.99はかからなくなり、販売手数料は18.5%から16%に、ストレージは100 GBから150 GBに増えます。それ以上保存する場合は、追加ストレージを100 GBあたり月$5から利用でき、カードが登録されていれば自動で追加されます（未登録の場合は、カードを登録するまで新しいアップロードが一時停止します。データは削除されません）。ギャラリーにはこれまでどおりロゴとブランドカラーが表示され、小さな“Powered by Photohawk”バッジが付きます。",
        },
        {
          q: "プランの容量を超えて保存したらどうなりますか？",
          a: "カードが登録されていれば、ストレージパックが自動で追加されます。100 GBあたり月$5からで、保存量をまかなう最も安い組み合わせで請求されます。カードが未登録の場合は、登録するまで新しいアップロードが一時停止します。データが削除されることはなく、ギャラリーでの販売も続きます。",
        },
        {
          q: "Enterprise を利用しています。どうなりますか？",
          a: "Enterprise は Max に名称が変わります。プランは同じで、名前だけが変わります。11月1日から販売手数料は13.5%から12%に、ストレージは1 TBから3 TBになります。新しい料金（月額$44.99ではなく$49）は、11月1日以降の最初の更新日から適用されます。",
        },
        {
          q: "ワークスペースには何人まで参加できますか？",
          a: "Lite は最大3人、Pro は最大10人、Max は無制限です。既にそれ以上のメンバーがいる場合も、アクセスはそのまま維持されます。上限が適用されるのは、新しく誰かを招待するときだけです。",
        },
        {
          id: "ftp",
          q: "FTPはどうなりますか？",
          a: "常時稼働の共有FTPサーバーは2026年11月1日に終了し、アップロードの標準はすべてのプランで Webアップローダーになりました。FTPが欠かせない場合は、すべてのプランで、ワークスペースのメンバーは誰でも既に Webアップローダーから自分専用のオンデマンドFTPサーバーを起動できます。アドレスは変わらない個人用のPhotohawkアドレスで、操作がないまま1時間たつと自動で停止します。",
        },
        {
          q: "個別契約は変わりますか？",
          a: "いいえ。既存の個別契約は、期間終了まで引き継がれます。",
        },
        {
          q: "プランを変更した場合、新しい販売手数料はいつから適用されますか？",
          a: "販売手数料の低いプランに変えると、そのプランでの最初の丸1か月から新しい手数料が適用されます。ダウングレードは月末に反映されます。",
        },
        {
          q: "カード手数料は変わりますか？",
          a: "いいえ。カード手数料と決済処理手数料は、販売手数料とは別で、現在と同じ仕組みです。",
        },
        {
          q: "Photohawk Friends & Family はプランに含まれますか？",
          a: "いいえ。Friends & Family はご家族の写真のための当社のアプリで、別のアプリとして近日公開予定です。Studio のプランには含まれません。ギャラリーの運営やアップロードには Photohawk Studio をお使いください。現在すべてのプランで Web ブラウザから使え、Mac、iPhone、iPad 向けの Studio アプリは2026年11月にリリース予定です。",
        },
      ],
    },
    cta: {
      heading: "新しい料金の詳細を見る",
      body: "プラン、ストレージ、無料ダウンロード、国別のカード手数料を1ページにまとめています。",
      button: "新しい料金を見る",
    },
  },

  app: {
    badge: "近日公開",
    platform: "まずは iPhone から",
    heading: "Photohawk Friends & Family、ご家族の写真のために",
    intro: "Friends & Family は、ご家族のすべての写真のための当社のアプリです。自分で撮った写真も、共有された写真もまとめられます。別のアプリとして近日公開予定で、Studio のプランには含まれません。",
    points: [
      { id: "private", title: "家族の写真を、プライベートに探す", body: "大切な人たちの写真を、スマートフォン上で整理して探せます。" },
      { id: "albums", title: "それぞれが自分の写真を見られるアルバム", body: "アルバムを共有すると、各メンバーはセルフィーで照合された自分が写っている写真だけを見られます。Photohawk のギャラリーと同じ技術です。" },
      { id: "event", title: "イベントでのみんなの写真を集める", body: "友人や家族が撮った写真を1か所にまとめられます。" },
      { id: "prints", title: "プリントとフォトブック", body: "お気に入りの写真をプリントやフォトブックにできます。" },
    ],
    cta: "お知らせを受け取る",
    ctaNote: "ウェイトリストはまもなく公開します。",
    phoneAlbum: "自分だけ",
    phoneAlbumCount: "写真12枚",
  },
};

export default ja;
